/**
 * CrossSurfaceNav — shared cross-surface nav links with quiet
 * brass active-state indicator.
 *
 * Founder ruling 19 Feb 2026 late evening:
 *   "Subtle brass active-state hint. Tiny brass dot below the active
 *    surface link. Feel like a small bookmark, not a tab bar. Black
 *    thought, brass promise."
 *
 * Used by:
 *   · /escape header (Escape.jsx)
 *   · /table + /enjoy shared TableHeader.jsx
 *
 * Mobile behaviour: each link wears `.lc-mobile-hide-on-narrow` so the
 * desktop nav vanishes under 640px and the ConciergeSurfaceMenu
 * hamburger takes over. The brass dot doesn't render on mobile because
 * the link itself is hidden.
 */
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useIsEmbedded } from "@/hooks/useIsEmbedded";

const ALL_SURFACES = [
    { path: "/table",       label: "/table"       },
    { path: "/stay",        label: "/stay"        },
    { path: "/fit",         label: "/fit"         },
    { path: "/care",        label: "/care"        },
    { path: "/exclusive",   label: "/exclusive"   },
    { path: "/airport",     label: "/airport"     },
    { path: "/mobility",    label: "/mobility"    },
    { path: "/enjoy",       label: "/enjoy"       },
    { path: "/escape",      label: "/escape"      },
    { path: "/dreamweaver", label: "/dreamweaver" },
    { path: "/club",        label: "/club"        },
    { path: "/anything",    label: "/anything"    },
];

/** True when `pathname` is currently on `surfacePath` or any sub-route. */
function isActiveSurface(pathname, surfacePath) {
    if (pathname === surfacePath) return true;
    if (pathname.startsWith(surfacePath + "/")) return true;
    return false;
}

export default function CrossSurfaceNav({
    /** Suppress the self-surface link entirely. Default false: founder
     *  rule says current surface stays in the nav but gets a brass dot. */
    hideSelf = false,
    /** testid prefix · each link gets `<prefix>-<surface>` */
    testidPrefix = "surface-nav",
    /** Visual size · "default" matches /escape's STYLES.label; "compact"
     *  matches TableHeader's 11px uppercase mono. */
    size = "default",
}) {
    // v3.47 — Hide the cross-surface nav entirely when embedded in the
    // Corporate Portal drawer · the parent already provides context.
    const embedded = useIsEmbedded();
    const { pathname } = useLocation();
    if (embedded) return null;
    const surfaces = hideSelf
        ? ALL_SURFACES.filter((s) => !isActiveSurface(pathname, s.path))
        : ALL_SURFACES;

    const styleForLink = size === "compact"
        ? {
              fontSize: 11,
              letterSpacing: "0.18em",
              color: "var(--t-ink3, #8A8071)",
              textDecoration: "none",
              fontFamily: "ui-monospace, monospace",
              textTransform: "uppercase",
          }
        : {
              fontSize: 11,
              letterSpacing: "0.18em",
              color: "var(--t-ink3, #8A8071)",
              textDecoration: "none",
              fontFamily: "ui-monospace, monospace",
              textTransform: "uppercase",
          };

    return (
        <>
            {surfaces.map((s) => {
                const active = isActiveSurface(pathname, s.path);
                return (
                    <Link
                        key={s.path}
                        to={s.path}
                        data-testid={`${testidPrefix}-${s.label.replace(/\W/g, "")}`}
                        data-active={active ? "true" : undefined}
                        aria-current={active ? "page" : undefined}
                        className="lc-mobile-hide-on-narrow lc-cross-surface-link"
                        style={{
                            ...styleForLink,
                            // Self-position so the brass dot pseudo-element
                            // can anchor below the link.
                            position: "relative",
                            // Small extra bottom padding so the dot sits
                            // ~6px below the baseline without shifting the
                            // header height.
                            paddingBottom: 6,
                        }}
                    >
                        {s.label}
                        {/* Quiet brass bookmark dot · only renders for the
                            current surface. Founder rule: "small brass
                            indicator, not a tab bar". */}
                        {active && (
                            <span
                                aria-hidden="true"
                                data-testid={`${testidPrefix}-${s.label.replace(/\W/g, "")}-active-dot`}
                                style={{
                                    position: "absolute",
                                    left: "50%",
                                    bottom: -2,
                                    transform: "translateX(-50%)",
                                    width: 4,
                                    height: 4,
                                    borderRadius: "50%",
                                    background: "var(--t-brass, #8B6914)",
                                    display: "block",
                                }}
                            />
                        )}
                    </Link>
                );
            })}
        </>
    );
}

export { ALL_SURFACES, isActiveSurface };
